import React, { useState, useEffect, useRef } from 'react';
import {
  Field,
  InjectedFormProps,
  FieldArray,
  getFormValues,
  change,
  isValid,
  initialize,
  isDirty
} from 'redux-form';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { compose } from 'ramda';
import { Grid, makeStyles, Tooltip } from '@material-ui/core';
import Fields from 'components/Fields';
import { Button } from 'components/Base';
import StoreProvider from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import { ButtonExpand, Prompt } from 'components/Base';
import Forms from 'components/Forms';
import api from 'api';
import Router from 'components/Router/Router';
import { CustomerType } from 'store/modules/Customers/types';
import { formatCurrency, Notify, unFormatPercent } from 'utils';
import { FieldCurrency } from 'components/Base';
import { unFormatCurrency, formatPercent } from 'utils';
import Icons from 'components/Icons';
import {
  ChargeType,
  FundAllocationType
} from 'store/modules/ExistingPolicies/types';
import ModelPortfolioCharges from '../ModelPortfolioCharges/ModelPortfolioCharges';
import ENV from 'consts/env';
import { TooltipIcon } from 'components/Base';
import { BoxWrapper } from 'components/Base';
import { findError } from 'components/Forms/Utils/findError';
import { existingPolicy } from 'components/Forms/Validators/existingPolicy';

const {
  FEATURE_FLAGS: { IS_MODEL_PORTFOLIO_CREATOR }
} = ENV;

const useStyles = makeStyles({
  singleForm: {
    height: '5.25rem',
    padding: '0 1rem',
    borderRadius: '25px'
  },
  policyBox: {
    width: '100%',
    backgroundColor: '#fffcf5',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
    borderRadius: '25px',
    marginBottom: '1rem'
  },
  fundsChargesContainer: {
    backgroundColor: '#e5e5e5',
    margin: 0,
    padding: '0.75rem',
    borderRadius: '25px'
  },
  btnBox: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'column-reverse'
  },
  icon: {
    cursor: 'pointer'
  },
  noteWrapper: {
    margin: '12px 0 6px 10px'
  },
  noteFieldWrapper: {
    padding: '1rem'
  },
  noteField: {
    overflowX: 'scroll',
    maxHeight: '145px'
  }
});

interface PolicyTypes {
  policyTypes: MetaType;
  policyProviders: MetaType;
  policyPlatforms: MetaType;
  fields: any;
  meta: any;
  policy: any;
  totalFundValue: number;
  policyValues: any;
  valid: boolean;
  customers: Array<CustomerType>;
  formName: string;
  changeField: any;
  policyIndex: number;
  removeLocalPolicy: (id: number, exist?: boolean) => void;
  markAsSaved: (policy: any) => void;
  updateExistingPolicy: (policy: any) => void;
  setExistingPolicies: any;
  clearExistingPolicies: any;
  chargeTypes: Array<MetaType>;
  arrayPush: (form: string, fieldArray: string, value: any) => void;
  isIoImport?: boolean;
  isIncomplete?: boolean;
  isComplete?: boolean;
  dirty: any;
}

const { useParams } = Router;
const { connect, selectors, actions } = StoreProvider;

const Policy = ({
  policyTypes,
  policyProviders,
  policyPlatforms,
  policyValues,
  customers,
  valid = true,
  formName,
  setExistingPolicies,
  changeField,
  removeLocalPolicy,
  policyIndex,
  markAsSaved,
  updateExistingPolicy,
  clearExistingPolicies,
  chargeTypes,
  isIoImport,
  isIncomplete,
  isComplete,
  dirty
}: InjectedFormProps & PolicyTypes) => {
  const history = useHistory();
  const [isModalVisible, setModalVisible] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null) as any;
  const [confirmLeave, setConfirmLeave] = useState(false);
  const dispatch = useDispatch();
  const [isOpenFundsCharges, setIsOpenFundsCharges] = useState(false);
  const [isDeletingModal, setIsDeletingModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aggregateOcf, setAggregateOcf] = useState(0);
  const [aggregateTradingCosts, setAggregateTradingCosts] = useState(0);
  const [aggregateTotalFundCharge, setAggregateTotalFundCharge] = useState(0);
  const [aggregateOneYPerformanceNew, setAggregateOneYPerformanceNew] =
    useState(0);
  const [aggregateThreeYPerformanceNew, setAggregateThreeYPerformanceNew] =
    useState(0);
  const [aggregateFiveYPerformanceNew, setAggregateFiveYPerformanceNew] =
    useState(0);

  const unblock = useRef<any>();

  const proceed = () => {
    if (!targetLocation) {
      return;
    }

    if (unblock.current) {
      unblock.current();
    }

    history.push(targetLocation);
  };

  useEffect(() => {
    if (valid && !dirty && unblock.current) {
      unblock.current();
    }

    if (confirmLeave) {
      proceed();
      return;
    }

    if (!valid || dirty) {
      unblock.current = history.block((location: any) => {
        setTargetLocation(location);
        setModalVisible(true);
        return false;
      });
    } else {
      setModalVisible(false);
      proceed();
    }
  }, [history, dirty, valid, confirmLeave]);

  const classes = useStyles();
  const totalFundValue = unFormatCurrency(policyValues.fund_value);
  const wrapAmount = policyValues.wrap_amount;
  const { portfolio } = useParams() as any;
  const { settings, is_processed } = policyValues;

  const getAggregateValue = (value: number) => {
    return value === null || value === undefined
      ? '-'
      : formatPercent(Number(value) * 100, true);
  };

  const getWeightedFigure = (
    entry: any,
    field: string,
    totalFundValue?: number | null,
    parentAllocation = 1
  ): number => {
    const allocation = parseFloat(entry.allocation || '0') * parentAllocation;
    const fundValue = entry.fund_value
      ? parseFloat(unFormatCurrency(entry.fund_value))
      : null;

    // Determine weight: Prefer fund_value, but fallback to allocation
    const weight =
      fundValue !== null && totalFundValue && totalFundValue > 0
        ? fundValue / totalFundValue
        : allocation;

    if (entry.fund) {
      const fieldValue = entry.fund_charge_overrides?.[0]?.[field]
        ? parseFloat(entry.fund_charge_overrides[0][field].replace('%', '')) /
          100
        : parseFloat(entry.fund[field]);

      return fieldValue * weight;
    }

    if (entry.model_portfolio) {
      if (entry.model_portfolio[field] !== undefined) {
        return parseFloat(entry.model_portfolio[field]) * weight;
      }

      if (entry.model_portfolio.fund_allocations) {
        const value = entry.model_portfolio.fund_allocations.reduce(
          (sum: number, subEntry: any) =>
            sum +
            getWeightedFigure(
              subEntry,
              field,
              fundValue,
              fundValue === null ? allocation : 1
            ),
          0
        );

        return value * weight;
      }
    }

    return 0;
  };

  const calculateAggregate = (
    allocations: any,
    field: string,
    totalFundValue: number
  ) => {
    return allocations
      .filter((entry: any) => entry.fund?.name !== 'Wrap Cash')
      .reduce(
        (total: any, entry: any) =>
          total + getWeightedFigure(entry, field, totalFundValue),
        0
      );
  };

  const getMpValue = (id: number) => {
    const allocation = policyValues.fund_allocations.find(
      (fundAllocation: any) => fundAllocation.model_portfolio?.id == id
    );

    if (!allocation) {
      return 0;
    }

    return Number(unFormatCurrency(allocation.fund_value));
  };

  useEffect(() => {
    const aggregatedCharges = {
      ocf: setAggregateOcf,
      trading_costs: setAggregateTradingCosts,
      one_y_performace: setAggregateOneYPerformanceNew,
      three_y_performace: setAggregateThreeYPerformanceNew,
      five_y_performace: setAggregateFiveYPerformanceNew
    } as any;

    Object.keys(aggregatedCharges).forEach((field: string) => {
      const allocationsExcludingWrap = policyValues.fund_allocations.filter(
        (allocation: any) => allocation.fund?.name !== 'Wrap Cash'
      );

      const wrap = policyValues.fund_allocations.find(
        (allocation: any) => allocation.fund?.name === 'Wrap Cash'
      );

      const figure = calculateAggregate(
        allocationsExcludingWrap,
        field,
        parseFloat(unFormatCurrency(policyValues.fund_value)) -
          (wrap ? parseFloat(unFormatCurrency(wrap.fund_value)) : 0)
      );

      if (isNaN(figure)) {
        aggregatedCharges[field](0);
        return;
      }

      aggregatedCharges[field](figure);
    });

    policyValues.fund_allocations
      .filter((allocation: any) => !!allocation.model_portfolio)
      .forEach((allocation: any) => {
        const index = policyValues.model_portfolio_charges.findIndex(
          (mpCharge: any) => mpCharge.id === allocation.model_portfolio.id
        );

        const charge = policyValues.model_portfolio_charges[index];

        if (charge.source_value === 1) {
          changeField(
            formName,
            `model_portfolio_charges[${index}].percentage_value`,
            formatPercent(
              (Number(unFormatCurrency(charge.cash_value)) /
                getMpValue(charge.id)) *
                100
            )
          );
        } else {
          changeField(
            formName,
            `model_portfolio_charges[${index}].cash_value`,
            formatCurrency(
              getMpValue(charge.id) *
                Number(unFormatPercent(charge.percentage_value) / 100)
            )
          );
        }
      });
  }, [policyValues.fund_allocations]);

  const recalculateCharges = (charges: any) => {
    const preperedCharges = Forms.Utils.prepareCharges({
      initialValues: charges.map((charge: any) => {
        return {
          ...charge,
          percentage_value: unFormatPercent(charge.percentage_value) / 100,
          cash_value:
            charge.cash_value === '-' ? 0 : unFormatCurrency(charge.cash_value)
        };
      }),
      chargeTypes: chargeTypes
    });
    changeField(formName, 'charges', preperedCharges);
  };

  useEffect(() => {
    setAggregateTotalFundCharge(aggregateOcf + aggregateTradingCosts);

    const aggregateModelPortfolioMonetaryCharge =
      policyValues.model_portfolio_charges.reduce(
        (acc: number, current: any) => {
          return acc + Number(unFormatCurrency(current.cash_value));
        },
        0
      );
    const aggregateModelPortfolioPercentageCharge =
      aggregateModelPortfolioMonetaryCharge / totalFundValue;

    const types = {
      1: aggregateOcf,
      2: aggregateTradingCosts,
      8: aggregateModelPortfolioPercentageCharge
    } as any;

    const charges = policyValues.charges;
    Object.keys(types).forEach((type: any) => {
      const chargeIndex = charges.findIndex(
        (charge: any) => charge.kind == type
      );

      const charge = charges[chargeIndex];

      charges.splice(chargeIndex, 1, {
        ...charge,
        percentage_value: formatPercent(Number(types[type]) * 100),
        cash_value: formatCurrency(
          parseFloat(unFormatCurrency(policyValues.fund_value)) *
            Number(types[type])
        )
      });
    });

    recalculateCharges(charges);
  }, [
    aggregateOcf,
    aggregateTradingCosts,
    aggregateOneYPerformanceNew,
    aggregateThreeYPerformanceNew,
    aggregateFiveYPerformanceNew,
    policyValues.model_portfolio_charges
  ]);

  useEffect(() => {
    const totalKind = 98;
    let percentageValue = 0;
    let cashValue = 0;
    policyValues.charges
      .filter((charge: any) => charge.kind !== totalKind)
      .forEach((charge: any) => {
        percentageValue += Number(unFormatPercent(charge.percentage_value));
        cashValue += Number(unFormatCurrency(charge.cash_value));
      });

    const charges = policyValues.charges;
    const totalChargeIndex = charges.findIndex(
      (charge: any) => charge.kind === totalKind
    );
    const totalCharge = charges[totalChargeIndex];
    charges.splice(totalChargeIndex, 1, {
      ...totalCharge,
      percentage_value: formatPercent(percentageValue),
      cash_value: formatCurrency(cashValue)
    });
  }, [policyValues.charges]);

  const isModelPortfolioInPolicy =
    !!policyValues &&
    policyValues.fund_allocations.find(
      (fund: FundAllocationType) => !!fund && !!fund.model_portfolio
    );

  const aggregateMPmanagmentCharge = policyValues.charges.find(
    (charge: ChargeType) => charge.kind === 8
  );

  const isPolicyValid =
    typeof policyValues.isValid === 'boolean' ? policyValues.isValid : true;

  const deletePolicy = () => {
    setIsDeleting(true);
    if (!policyValues.id || (isIoImport && isIncomplete))
      return removeLocalPolicy(policyIndex);
    return api
      .deleteExistingPolicy({ portfolio, policyId: policyValues.id })
      .then(() => {
        return api.getExistingPolicies({ portfolio }).then(({ data }: any) => {
          clearExistingPolicies();
          removeLocalPolicy(policyIndex);
          setExistingPolicies(data);
          setIsDeleting(false);
        });
      })
      .catch(err => {
        Notify.error(err.response.data.message);
        setIsDeleting(false);
      });
  };

  const displayError = (err: any) => {
    if (err.response.status === 400) {
      const { key, error } = findError(err.response.data);
      const readableKey = key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());

      Notify.error(
        'Could not save policy',
        `Field: ${readableKey}, Error: ${error}`
      );
    } else {
      Notify.error(err.response.data.message);
    }
  };

  const savePolicy = () => {
    setIsProcessing(true);
    const isNewPolicy = !policyValues.id;

    const validationErrors = existingPolicy(policyValues);

    if (validationErrors?.settings?.funds?.errors) {
      setIsProcessing(false);
      Notify.error('Validation Error', validationErrors.settings.funds.errors);
      return;
    }

    const preparedPolicy =
      Forms.Utils.prepareSubmit.existingPolicies(policyValues);

    if (isNewPolicy) {
      return api
        .addExistingPolicy({
          portfolio,
          policy: preparedPolicy
        })
        .then(({ data }) => {
          changeField(formName, 'id', data.id);
          setTimeout(() => {
            dispatch(
              initialize(formName, { ...policyValues, id: data.id }) as any
            );
          }, 100);
          markAsSaved(
            !isIncomplete
              ? data
              : {
                  policy: data,
                  io_id: policyValues.io_id
                }
          );
          setIsProcessing(false);
          setIsOpenFundsCharges(false);
          Notify.success(
            'Existing policy saved',
            'New Policy was successfully added'
          );
        })
        .catch(err => {
          setIsProcessing(false);
          displayError(err);
        });
    }

    return api
      .editExistingPolicy({ portfolio, policy: preparedPolicy })
      .then(({ data }) => {
        updateExistingPolicy(data);
        setIsProcessing(false);
        setIsOpenFundsCharges(false);
        setTimeout(() => {
          dispatch(initialize(formName, policyValues) as any);
        }, 100);
        Notify.success(
          'Existing policy saved',
          'Policy was successfully updated'
        );
      })
      .catch(err => {
        setIsProcessing(false);
        if (err.response) {
          displayError(err);
        }
      });
  };

  const updateTransferValue = (e: any) => {
    policyValues.transfer_value = formatCurrency(
      Number(e.target.value.replace(/[£|,]/g, ''))
    );
  };
  const customerSelections = isIoImport
    ? customers.reduce((acc: any, customer: CustomerType) => {
        if (customer.id === policyValues.owner) {
          acc.push({
            name: customer.display_name,
            value: customer.id
          });
        }
        return acc;
      }, [])
    : customers.map((customer: CustomerType) => ({
        name: customer.display_name,
        value: customer.id
      }));

  return (
    <div className={classes.policyBox} id={`existing-policy-${formName}`}>
      <Prompt
        isOpen={isModalVisible}
        title={`You have unsaved changes to policy #${policyIndex + 1}. Are you sure you want to leave?`}
        confirmAction={() => setConfirmLeave(true)}
        cancelAction={() => {
          setTargetLocation(null);
          setModalVisible(false);
        }}
        isProcessing={false}
      />
      <Prompt
        isOpen={isDeletingModal}
        title={`Are you sure you want to remove this policy?`}
        confirmAction={() => {
          deletePolicy();
          setIsDeletingModal(false);
        }}
        cancelAction={() => setIsDeletingModal(false)}
        isProcessing={isDeleting}
      />
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        className={classes.singleForm}
        style={{ backgroundColor: isIncomplete ? '#fffcf5' : '#fff' }}
      >
        <Grid item xs={1}>
          <Field
            disabled={customerSelections.length === 1}
            name={`owner`}
            testId={`owner-${formName}`}
            shrink
            fullWidth
            validate={Fields.Validators.required}
            displayEmpty
            options={customerSelections}
            component={Fields.Select}
            placeholder=""
            label="Policy owner(s)"
          />
        </Grid>

        <Grid item xs={1}>
          <Field
            name={`platform`}
            testId={`platform-${formName}`}
            shrink
            fullWidth
            validate={Fields.Validators.required}
            displayEmpty
            options={policyPlatforms}
            component={Fields.SelectAuto}
            label="Platform"
          />
        </Grid>

        <Grid item xs={2}>
          <Field
            name={`provider`}
            testId={`provider-${formName}`}
            shrink
            fullWidth
            validate={Fields.Validators.required}
            displayEmpty
            options={policyProviders}
            component={Fields.SelectAuto}
            label="Policy provider"
          />
        </Grid>

        <Grid item xs={1}>
          <Field
            name={`type`}
            testId={`type-${formName}`}
            shrink
            fullWidth
            validate={Fields.Validators.required}
            displayEmpty
            options={policyTypes}
            component={Fields.SelectAuto}
            label="Policy type"
          />
        </Grid>

        <Grid item>
          <Field
            name={`number`}
            testId={`number-${formName}`}
            shrink
            fullWidth
            validate={Fields.Validators.required}
            component={Fields.Text}
            label="Policy number"
          />
        </Grid>

        <Grid item xs={1}>
          <FieldCurrency
            onChange={updateTransferValue}
            name={`fund_value`}
            testId={`fund_value-${formName}`}
            shrink
            fullWidth
            clearfieldinform={formName}
            type="text"
            validate={Fields.Validators.required}
            component={Fields.Text}
            label="Fund value"
          />
        </Grid>

        <Grid item xs={1}>
          <FieldCurrency
            name={`transfer_value`}
            testId={`transfer_value-${formName}`}
            shrink
            fullWidth
            clearfieldinform={formName}
            validate={Fields.Validators.required}
            component={Fields.Text}
            label="Transfer value"
          />
        </Grid>

        <Grid item>
          <ButtonExpand
            disabled={!valid && !is_processed}
            style={{
              height: '42px',
              marginRight: '0.5rem'
            }}
            isOpen={isOpenFundsCharges}
            onClick={() => setIsOpenFundsCharges(!isOpenFundsCharges)}
            id={`policy-${policyIndex}-add-funds`}
          >
            Manage Policy
          </ButtonExpand>
          <Button
            disabled={!valid || is_processed}
            isLoading={isProcessing}
            onClick={() => savePolicy()}
            style={{ height: '42px', marginTop: '1rem', marginBottom: '1rem' }}
          >
            {is_processed ? 'Locked' : 'Save'}
          </Button>
        </Grid>
        {isIncomplete && (
          <Grid>
            <TooltipIcon
              text={
                'This policy contains unsupported funds. Review all added funds'
              }
              icon="warn"
            />
          </Grid>
        )}
        <Grid
          item
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Icons.LoadingWrapper isLoading={isDeleting}>
            {is_processed ? (
              <Tooltip title="Case is processed" arrow>
                <Icons.TimeIcon className={classes.icon} width="2.2rem" />
              </Tooltip>
            ) : (
              <Tooltip title="Delete Policy" arrow>
                <Icons.DeleteIcon
                  width="2.2rem"
                  className={classes.icon}
                  onClick={() => setIsDeletingModal(true)}
                />
              </Tooltip>
            )}
          </Icons.LoadingWrapper>
          {!isPolicyValid && (
            <TooltipIcon icon="warn" text="Policy requires user refinement" />
          )}
        </Grid>
      </Grid>
      {isOpenFundsCharges && (
        <Grid container className={classes.fundsChargesContainer}>
          <Grid item xs={12}>
            <FieldArray
              name={`fund_allocations`}
              component={Forms.ManagePolicies.Funds}
              totalFundValue={totalFundValue}
              rerenderOnEveryChange
              formName={formName}
              wrapAmount={wrapAmount}
              allowPercentageValue={false}
              aggregateOcfChargeValue={getAggregateValue(aggregateOcf)}
              aggregateFundChargeValue={getAggregateValue(
                aggregateTradingCosts
              )}
              aggregateTotalFundChargeValue={getAggregateValue(
                aggregateTotalFundCharge
              )}
              aggregateOneYPerformanceValue={getAggregateValue(
                aggregateOneYPerformanceNew
              )}
              aggregateThreeYPerformanceValue={getAggregateValue(
                aggregateThreeYPerformanceNew
              )}
              aggregateFiveYPerformanceValue={getAggregateValue(
                aggregateFiveYPerformanceNew
              )}
              policyValues={policyValues}
              modelPortfolioCharges={policyValues.model_portfolio_charges}
              isIncomplete={isIncomplete}
              isComplete={isComplete}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldArray
              name={`charges`}
              formName={formName}
              component={Forms.ManagePolicies.Charges}
              rerenderOnEveryChange
              totalFundValue={totalFundValue}
              allowPercentageValue={settings.chargesAllowPercentage}
              isModelPortfolioInPolicy={isModelPortfolioInPolicy}
            />
          </Grid>
          <Grid item xs={6}>
            {IS_MODEL_PORTFOLIO_CREATOR && isModelPortfolioInPolicy && (
              <FieldArray
                name={'model_portfolio_charges'}
                component={ModelPortfolioCharges}
                formName={formName}
                aggregateMPmanagmentCharge={aggregateMPmanagmentCharge}
                getMpValue={getMpValue}
              />
            )}
            <BoxWrapper title="Notes" className={classes.noteWrapper}>
              <div className={classes.noteFieldWrapper}>
                <Field
                  className={classes.noteField}
                  name="additional_info"
                  shrink
                  fullWidth
                  component={Fields.Text}
                  multiline
                  minRows={7}
                  label="Additional policy information"
                />
              </div>
            </BoxWrapper>
          </Grid>
          <Grid item xs={12} className={classes.btnBox}>
            <Button
              disabled={!valid || is_processed}
              isLoading={isProcessing}
              style={{ marginTop: '1rem', marginBottom: '1rem' }}
              onClick={() => savePolicy()}
            >
              {is_processed ? 'Locked' : 'Save'}
            </Button>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default compose(
  connect(
    (state: any, ownProps: any) => ({
      policyTypes: selectors.metaSelectors.policyTypes(state),
      policyProviders: selectors.metaSelectors.policyProviders(state),
      policyPlatforms: selectors.metaSelectors.policyPlatforms(state),
      policyValues: getFormValues(`${ownProps.formName}`)(state),
      valid: isValid(`${ownProps.formName}`)(state),
      dirty: isDirty(`${ownProps.formName}`)(state),
      customers: selectors.customersSelectors.customers(state),
      chargeTypes: selectors.metaSelectors.chargeTypes(state)
    }),
    {
      changeField: change,
      initialize,
      setExistingPolicies: actions.caseActions.setExistingPolicies,
      updateExistingPolicy: actions.caseActions.updateExistingPolicy,
      setIsImportingPolicies:
        actions.PolicyImportActions.setIsImportingPolicies,
      clearExistingPolicies: actions.caseActions.clearExistingPolicies
    }
  )
)(Policy);
